import { Box, Chip, Typography, useTheme } from '@mui/material';
import { createElement, Fragment } from 'react';
import { typeMap } from '../FormFields';
import { tokens } from '../../../global/theme/tokens';

/**
 * A sub-layout inside a form: renders its `field.fields` through the same type
 * map as FormFields, but into its own wrapper. The default is a flexbox column,
 * so a single ExoForm grid can hold independently flowing columns. With
 * `variant: 'card'` the group renders as a titled card — header row with the
 * label, optional extra header fields (`field.headerFields`, e.g. a checkbox)
 * and an optional amount badge — with the children as the card body
 * (`field.className` overrides the body layout, default padded column).
 *
 * The children are rendered bare (no highlight container); field types that can
 * show their own error text keep doing so via the `errors` prop. For types that
 * cannot (e.g. `project`), set `showError: true` on the child to render its
 * error message as a text line below it.
 *
 * With `errorsVisible: false` the children see no errors, so a form can keep
 * validating silently and only reveal the errors after a failed save attempt.
 */
const TypeFieldGroup = ({
  field,
  dataCollection,
  startDataCollection,
  errors = {},
  updateValidationOnChange,
  onPreventEnter,
  param,
  size,
  validationSchema,
  onError,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const visibleErrors = field.errorsVisible === false ? {} : errors;

  const renderSubFields = (subFields) =>
    (subFields || []).map((subField, index) => {
      if (!typeMap[subField.type])
        return console.error(`INVALIDE Form Field Type: ${subField.type}`);

      if (subField.hidden) return null;
      return (
        <Fragment key={subField.key || `${subField.type}${index}`}>
          {createElement(typeMap[subField.type], {
            dataCollection: dataCollection || {},
            startDataCollection,
            field: subField,
            errors: visibleErrors,
            updateValidationOnChange,
            onPreventEnter,
            param,
            size: subField.size || size,
            validationSchema,
            onError,
          })}
          {subField.showError && visibleErrors[subField.key] && (
            <Typography variant="body2" sx={{ color: colors.error }}>
              {visibleErrors[subField.key]}
            </Typography>
          )}
        </Fragment>
      );
    });

  const gridColumn = field.colspan
    ? `span ${field.colspan} / span ${field.colspan}`
    : 'auto';

  if (field.variant !== 'card') {
    return (
      <Box
        className={field.className || 'flex flex-col gap-4 w-full'}
        sx={{ gridColumn }}
      >
        {renderSubFields(field.fields)}
      </Box>
    );
  }

  return (
    <Box
      className="w-full rounded-xl overflow-hidden flex flex-col"
      sx={{
        gridColumn,
        backgroundColor: colors.card,
        border: `1px solid ${colors.selected}`,
      }}
    >
      <Box
        className="flex items-center gap-3 px-4 py-2 flex-wrap"
        sx={{ borderBottom: `1px solid ${colors.selected}` }}
      >
        <Typography variant="h6" className="whitespace-nowrap">
          {field.label}
        </Typography>
        {renderSubFields(field.headerFields)}
        {field.badge != null && (
          <Chip
            size="small"
            className="ml-auto"
            label={field.badge}
            sx={{ bgcolor: colors.bgInput }}
          />
        )}
      </Box>
      <Box className={field.className || 'flex flex-col gap-4 p-4'}>
        {renderSubFields(field.fields)}
      </Box>
    </Box>
  );
};

export default TypeFieldGroup;
